import { useQuery } from "@tanstack/react-query";

import {
  getAllMedicalReports,
  getLongTermSignedUrlForManualReport,
  getSignedUrlForManualReport,
} from "@/features/imagings/services/medicalReportsService";
import type {
  ManualReport,
  MedicalReport,
  Report,
} from "@/features/imagings/types/reports";

/**
 * Hook para obtener todos los informes médicos de un estudio
 * @param imagingId - ID del estudio de imagen
 * @param accessionNumber - Número de acceso del estudio
 * @param institutionId - ID de la institución
 * @param enabled - Si la query debe ejecutarse
 * @returns Query result con informes RIS y manuales
 */
export const useMedicalReports = (
  imagingId: string,
  accessionNumber: string,
  institutionId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["medicalReports", imagingId, accessionNumber, institutionId],
    queryFn: () => getAllMedicalReports(imagingId, accessionNumber, institutionId),
    enabled: enabled && !!imagingId && !!accessionNumber && !!institutionId,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });
};

/**
 * Hook para obtener una URL firmada para un informe manual
 * @param idFile - ID del archivo
 * @param idStudy - ID del estudio
 * @param hours - Duración de la URL en horas
 * @param enabled - Si la query debe ejecutarse
 * @returns Query result con la URL firmada
 */
export const useSignedUrlForManualReport = (
  idFile: string,
  idStudy: string,
  hours: number = 24,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["signedUrl", idFile, idStudy, hours],
    queryFn: () => getSignedUrlForManualReport(idFile, idStudy, hours),
    enabled: enabled && !!idFile && !!idStudy,
    staleTime: 30 * 60 * 1000, // 30 minutos (las URLs firmadas expiran)
    gcTime: 60 * 60 * 1000, // 1 hora
  });
};

/**
 * Hook para obtener una URL firmada de larga duración para compartir informes manuales
 * @param idFile - ID del archivo
 * @param idStudy - ID del estudio
 * @param enabled - Si la query debe ejecutarse
 * @returns Query result con la URL firmada de larga duración
 */
export const useLongTermSignedUrlForManualReport = (
  idFile: string,
  idStudy: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["longTermSignedUrl", idFile, idStudy],
    queryFn: () => getLongTermSignedUrlForManualReport(idFile, idStudy),
    enabled: enabled && !!idFile && !!idStudy,
    staleTime: 24 * 60 * 60 * 1000, // 24 horas
    gcTime: 24 * 60 * 60 * 1000, // 24 horas
  });
};

/**
 * Hook para obtener un informe específico por ID
 * @param reports - Array de informes (RIS y manuales)
 * @param reportId - ID del informe a buscar
 * @returns El informe encontrado o null
 */
export const useReportById = (
  reports: { ris: MedicalReport[]; manual: ManualReport[] } | undefined,
  reportId: string
): Report | null => {
  if (!reports) return null;

  // Buscar en informes RIS
  const risReport = reports.ris.find((report) => report.id === reportId);
  if (risReport) return risReport;

  // Buscar en informes manuales
  const manualReport = reports.manual.find((report) => report.id === reportId);
  if (manualReport) return manualReport;

  return null;
};

/**
 * Hook para obtener informes filtrados
 * @param reports - Array de informes (RIS y manuales)
 * @param filters - Filtros a aplicar
 * @returns Array de informes filtrados
 */
export const useFilteredReports = (
  reports: { ris: MedicalReport[]; manual: ManualReport[] } | undefined,
  filters: {
    type?: "ris" | "manual" | "all";
    status?: string;
    dateFrom?: Date;
    dateTo?: Date;
  }
): Report[] => {
  if (!reports) return [];

  let filteredReports: Report[] = [];

  // Aplicar filtro de tipo
  if (filters.type === "ris") {
    filteredReports = [...reports.ris];
  } else if (filters.type === "manual") {
    filteredReports = [...reports.manual];
  } else {
    filteredReports = [...reports.ris, ...reports.manual];
  }

  // Aplicar filtro de estado (solo para informes RIS)
  if (filters.status) {
    filteredReports = filteredReports.filter((report) => {
      if (report.type === "ris") {
        return report.status === filters.status;
      }
      return true; // Los informes manuales no tienen estado
    });
  }

  // Aplicar filtros de fecha
  if (filters.dateFrom) {
    filteredReports = filteredReports.filter((report) => {
      if (filters.dateFrom) {
        return report.created_at >= filters.dateFrom;
      }
      return true;
    });
  }

  if (filters.dateTo) {
    filteredReports = filteredReports.filter((report) => {
      if (filters.dateTo) {
        return report.created_at <= filters.dateTo;
      }
      return true;
    });
  }

  // Ordenar por fecha de creación (más reciente primero)
  return filteredReports.sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
};

/**
 * Hook para obtener estadísticas de informes
 * @param reports - Array de informes (RIS y manuales)
 * @returns Estadísticas de los informes
 */
export const useReportsStats = (
  reports: { ris: MedicalReport[]; manual: ManualReport[] } | undefined
) => {
  if (!reports) {
    return {
      total: 0,
      ris: 0,
      manual: 0,
      completed: 0,
      draft: 0,
      verified: 0,
      amended: 0,
    };
  }

  const risReports = reports.ris;
  const manualReports = reports.manual;

  const stats = {
    total: risReports.length + manualReports.length,
    ris: risReports.length,
    manual: manualReports.length,
    completed: 0,
    draft: 0,
    verified: 0,
    amended: 0,
  };

  // Contar estados solo para informes RIS
  risReports.forEach((report) => {
    switch (report.status) {
      case "completed":
        stats.completed++;
        break;
      case "draft":
        stats.draft++;
        break;
      case "verified":
        stats.verified++;
        break;
      case "amended":
        stats.amended++;
        break;
    }
  });

  return stats;
};
